import { buildAuthorityLabel, buildAuthorityStats, resolveLatestContextAuthority } from './context-authority.js';
import { resolveLatestContextActivity } from './context-activity.js';
import { buildTokenLimitProjection } from './token-limit.js';

const objectValue = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : {}
);

const latestContextUsage = (items) => {
  const latest = [...items].reverse().find((item) => item?.type === 'message'
    && item.role === 'assistant' && item.contextUsage);
  return latest?.contextUsage ?? null;
};

const sessionCapacity = (sessionDetail) => {
  const session = objectValue(sessionDetail);
  const run = objectValue(session.active_run ?? session.latest_run);
  return run.usage_projection?.context_capacity ?? null;
};

export function buildContextUsageViewModel({ timelineItems, sessionDetail, contextUsage }, formatters) {
  const items = Array.isArray(timelineItems) ? timelineItems : [];
  const usage = contextUsage ?? latestContextUsage(items);
  const live = objectValue(usage);
  const capacity = objectValue(live.turn_usage).request_id
    ? live.user_capacity : (sessionCapacity(sessionDetail) ?? live.user_capacity ?? null);
  const projection = buildTokenLimitProjection({ usage, capacity }, formatters);
  const contextAuthority = resolveLatestContextAuthority(items);
  const contextStats = resolveSessionContextStats(sessionDetail) ?? buildAuthorityStats(contextAuthority);
  const contextActivity = resolveLatestContextActivity(items);
  const lines = [projection.tokenLabel, projection.budgetLine, buildAuthorityLabel(contextAuthority),
    formatters?.buildContextStatsLabel?.(contextStats), ...formatters?.buildContextActivityLines?.(contextActivity) ?? []].filter(Boolean);
  return {
    usage,
    contextActivity, contextAuthority, contextStats,
    turnUsage: live.turn_usage ?? null,
    tokenLimit: projection.displayTokenLimit,
    ...projection,
    lines,
    text: lines.join(' · '),
  };
}

const resolveObject = value => value && typeof value === 'object' && !Array.isArray(value) ? value : null;

function countList(value) {
  return Array.isArray(value) ? value.length : 0;
}

function resolveSessionContextStats(sessionDetail) {
  const context = resolveObject(sessionDetail?.context);
  if (!context) {
    return null;
  }
  const stats = {
    factsCount: countList(context.facts),
    unknownsCount: countList(context.unknowns),
    evidenceCount: countList(context.evidence),
    turnsCount: countList(context.turns),
    artifactContextCount: countList(context.artifact_context),
    ...archiveContextStats(context.archive_summary),
    updatedAt: contextUpdatedAt(sessionDetail, context),
  };
  const hasContextData = [
    stats.factsCount, stats.unknownsCount, stats.evidenceCount,
    stats.turnsCount, stats.artifactContextCount,
  ].some((count) => count > 0) || stats.hasArchiveSummary || String(context.session_summary || '').trim();
  return hasContextData ? stats : null;
}

function archiveContextStats(value) {
  const archiveSummary = resolveObject(value);
  const relationshipGraph = resolveObject(archiveSummary?.relationship_graph) || {};
  const metrics = resolveObject(archiveSummary?.metrics) || {};
  return {
    hasArchiveSummary: Boolean(archiveSummary),
    graphNodesCount: countList(relationshipGraph.nodes),
    graphEdgesCount: countList(relationshipGraph.edges),
    completenessScore: Number(metrics.completeness_score || 0),
  };
}

function contextUpdatedAt(sessionDetail, context) {
  return sessionDetail?.updatedAt || sessionDetail?.updated_at || context.updated_at || '';
}
